/**
 * THE dayjs entry point for the whole product — the only place plugins are
 * registered, in any package, and the only module any call site imports.
 *
 * There are no per-package re-exports: `apps/web` and `packages/tasks` import this
 * path directly. Nothing may import 'dayjs' itself; an ESLint `no-restricted-imports`
 * rule enforces that, with this file as the sole exception (`overrides` in
 * `packages/shared/.eslintrc.cjs`).
 *
 * Where that rule actually runs: `apps/web/.eslintrc.cjs`,
 * `packages/tasks/.eslintrc.cjs` and — since the PR #182 review — this package's own
 * `.eslintrc.cjs`, which was the gap: `packages/shared` had no ESLint config and no
 * `lint` script, so `pnpm lint` (`pnpm -r run lint`) skipped the very package this
 * module lives in. The one runtime still outside ESLint is `supabase/functions`
 * (Deno, no config); what protects it is `deno-import-graph.test.ts` in this
 * package, which walks the edge-function import graph and resolves every specifier.
 *
 * WHY ONE CONFIG AND NOT ONE PER PACKAGE
 *
 * `dayjs.extend()` mutates the dayjs module object, and pnpm resolves every package
 * in this monorepo to the SAME physical copy — so a plugin registered anywhere is
 * visible to every importer in that runtime, and per-package configs buy no
 * isolation at all. What they do cost is determinism: with several configs, whether
 * a given module gets `.utc()` or `.tz()` depends on which config happened to load
 * first. That is exactly why `formatters.ts` used to call `extend(utc)` for itself.
 * One config makes the plugin set a property of the product, not of the import path.
 *
 * The set below is what the product actually calls: `.utc()`, `.tz()` for the
 * business timezone, and strict `customParseFormat` parsing. `relativeTime` was
 * registered by the old web config and is deliberately NOT here — nothing in the
 * product calls `fromNow()`/`toNow()`, so carrying it only grew the browser bundle.
 * Re-adding it is a one-line change here, and a missing plugin is a TYPE error at
 * the call site rather than a runtime surprise.
 *
 * IMPORTANT — this module is loaded by all three runtimes: the Vite browser bundle,
 * the Node worker in `packages/tasks`, and the Deno edge functions (via
 * `_shared/ledgersync-finalize.ts` → `business-date.ts` → here). Deno resolves the
 * specifiers below through `supabase/functions/import_map.json`, which maps them to
 * `npm:dayjs@1.11.20`; if those two entries are ever removed, every edge function
 * that touches a shared date helper breaks at deploy time.
 *
 * The plugin imports carry an explicit `.js` extension, and that is load-bearing.
 * dayjs publishes no `exports` map, so Deno's npm resolution cannot infer the
 * extension for a subpath: `dayjs/plugin/utc` TYPE-CHECKS CLEAN and then dies at
 * RUNTIME with ERR_MODULE_NOT_FOUND, which means `deno check` alone does not catch
 * it — only actually running the module does. `moduleResolution: "bundler"` (used by
 * all three tsconfigs here) maps the `.js` specifier back to the plugin's `.d.ts`,
 * so Node/Vite types stay intact.
 *
 * `packages/shared/package.json` lists this module EXPLICITLY in `exports`
 * (`"./utils/dayjs": "./src/utils/dayjs.ts"`) rather than leaning on the `"./*":
 * "./src/*"` wildcard, which resolves to an extensionless path. Vite retries
 * extensions and would cope; esbuild (what `trigger.dev deploy` bundles
 * `packages/tasks` with) and plain Node follow `exports` strictly and would not — and
 * that failure would surface at deploy, not in any local gate.
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import timezone from 'dayjs/plugin/timezone.js'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'

// `timezone` is built on `utc`, so the order of these two extends matters.
dayjs.extend(utc)
// Gives `dayjs().tz('America/Chicago')` — the only way to ask what calendar day it
// is in the timezone the company's books are kept in, rather than in UTC or in
// whatever timezone the host happens to be configured with.
dayjs.extend(timezone)
// Required to pass an explicit format string plus strict mode to dayjs(): without
// it `dayjs.utc(s, 'YYYY-MM-DD', true)` silently degrades to lenient ISO parsing
// and stops rejecting non-existent days such as 2026-02-30.
dayjs.extend(customParseFormat)

export default dayjs
